import { Injectable, signal, effect } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

export type ThemeMode = 'dark' | 'light';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly STORAGE_KEY = 'tidy_theme_mode';
  private readonly USER_SET_KEY = 'tidy_theme_user_set';
  private readonly FORCE_DARK_KEY = 'tidy_force_dark_mode';

  readonly themeMode = signal<ThemeMode>(this.getInitialTheme());

  constructor() {
    if (localStorage.getItem(this.FORCE_DARK_KEY) === 'true') {
      this.applyTheme('dark');
    } else {
      this.applyTheme(this.themeMode());
    }
    effect(() => {
      const mode = this.themeMode();
      if (localStorage.getItem(this.FORCE_DARK_KEY) !== 'true') {
        this.applyTheme(mode);
      }
      localStorage.setItem(this.STORAGE_KEY, mode);
    });
  }

  forceDarkMode(): void {
    localStorage.setItem(this.FORCE_DARK_KEY, 'true');
    this.applyTheme('dark');
  }

  clearForcedDarkMode(): void {
    if (localStorage.getItem(this.FORCE_DARK_KEY) !== 'true') { return; }
    localStorage.removeItem(this.FORCE_DARK_KEY);
    this.applyTheme(this.themeMode());
  }

  isDarkMode(): boolean {
    return this.themeMode() === 'dark' || localStorage.getItem(this.FORCE_DARK_KEY) === 'true';
  }

  /** Re-apply the theme-appropriate status bar style (e.g. after platform ready). */
  refreshStatusBar(): void {
    this.syncStatusBar(this.isDarkMode() ? 'dark' : 'light');
  }

  toggleTheme(): void {
    localStorage.setItem(this.USER_SET_KEY, 'true');
    this.themeMode.set(this.isDarkMode() ? 'light' : 'dark');
  }

  setTheme(mode: ThemeMode): void {
    localStorage.setItem(this.USER_SET_KEY, 'true');
    this.themeMode.set(mode);
  }

  private applyTheme(mode: ThemeMode): void {
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(`${mode}-mode`);
    this.syncStatusBar(mode);
  }

  /**
   * Keep the native status bar readable in both themes. Capacitor semantics:
   * Style.Dark = dark bar background with WHITE text, Style.Light = light bar
   * background with DARK text. The app previously hardcoded Style.Dark, which
   * left white status text on the light-grey light-mode background (unreadable).
   */
  private syncStatusBar(mode: ThemeMode): void {
    if (!Capacitor.isNativePlatform()) { return; }
    StatusBar.setStyle({ style: mode === 'light' ? Style.Light : Style.Dark })
      .catch(() => { /* plugin unavailable (e.g. tests); ignore */ });
    if (Capacitor.getPlatform() === 'android') {
      // Match the bar to the app background (light: --tidy-bg-primary #F5F5F5,
      // dark: #000000). Android-only API; iOS derives the bar from the webview.
      StatusBar.setBackgroundColor({ color: mode === 'light' ? '#F5F5F5' : '#000000' })
        .catch(() => { /* ignore */ });
    }
  }

  private getInitialTheme(): ThemeMode {
    if (localStorage.getItem(this.USER_SET_KEY) === 'true') {
      return (localStorage.getItem(this.STORAGE_KEY) as ThemeMode) || 'dark';
    }
    return 'dark';
  }
}
